import React, { useCallback, useState } from 'react';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import NavigationLink from './NavigationLink';
import { useAppSelector } from '../../redux/hooks';
import { userDataSelectors } from '../../redux/userData';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';

import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { paths } from '../../constants/paths';
import SecurityIcon from '@mui/icons-material/Security';
import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowUpRounded,
  MenuRounded,
  Logout as LogoutIcon,
} from '@mui/icons-material';

import HistoryIcon from '@mui/icons-material/History';
import { SowManagementIcon } from '../../components/svg/SowManagementIcon';

// Firebase logout imports
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { AuthService } from '../../services/auth-service';

export const NAVIGATION_TEST_ID = 'navigation-test-id';

interface NavigationProps {
  open: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ open }: NavigationProps) => {
  const user = useAppSelector(userDataSelectors.getUserData);
  const [isSecondaryNavOpen, setNavOpen] = useState<boolean>(false);

  const handleSecondaryOptionsToggle = useCallback(() => {
    setNavOpen(!isSecondaryNavOpen);
  }, [isSecondaryNavOpen, setNavOpen]);

  // Firebase logout handler
  const handleLogout = useCallback(async () => {
    try {
      AuthService.clearToken();
      await signOut(auth);
      // Optionally, add a redirect or cleanup here
      window.location.reload(); // force reload to clear app state
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, []);

  if (!user) {
    return null;
  }

  const {
    canViewListOfContractors,
    canManageKpiDefinitions,
    canSubmitKpiValues,
    canApproveKpiValues,
    canGenerateReport,
    canViewMyMetrics,
    hasAccounts,
    isBillRateManager,
    isCompensationManager,
    isFinance,
    isRevenueSummaryAdmin,
  } = user.permissions;

  const hasBonusesAccess =
    canViewMyMetrics ||
    canViewListOfContractors ||
    canSubmitKpiValues ||
    canApproveKpiValues ||
    canGenerateReport;

  const hasMarginTrackerAccess =
    hasAccounts && (isBillRateManager || isCompensationManager);

  return (
    <List data-testid={NAVIGATION_TEST_ID} sx={{ mx: 0, py: 0, px: 1 }}>
      {hasBonusesAccess && (
        <>
          {open && (
            <Typography variant="body2" ml={2.5} mt={1.5} mb={0.5}>
              Bonuses
            </Typography>
          )}
          {canViewMyMetrics && (
            <NavigationLink
              to={paths.myMetrics}
              icon={<HowToRegOutlinedIcon />}
              title="My metrics"
              open={open}
            />
          )}
          {canViewListOfContractors && (
            <NavigationLink
              to={paths.contractors}
              icon={<ContactsOutlinedIcon />}
              title="Teams"
              open={open}
            />
          )}
          {canSubmitKpiValues && (
            <NavigationLink
              to={paths.submissions}
              icon={<AddchartOutlinedIcon />}
              title="Monthly results"
              open={open}
            />
          )}
          {canApproveKpiValues && (
            <NavigationLink
              to={paths.approvals}
              icon={<EventAvailableOutlinedIcon />}
              title="Approvals"
              open={open}
            />
          )}
          {(canGenerateReport || isCompensationManager) && (
            <NavigationLink
              to={paths.reports}
              icon={<AutoStoriesOutlinedIcon />}
              title="Reports"
              open={open}
            />
          )}
          {canViewListOfContractors && (
            <NavigationLink
              to={
                'https://docs.google.com/document/d/1wU0T3KZnZU8JQ7g0YGr5hF5QzeysxYLFRTGCpKkD7p0/edit'
              }
              icon={<BookOutlinedIcon />}
              title="Guidelines ↗"
              isExternal={true}
              open={open}
            />
          )}
        </>
      )}
      {(canManageKpiDefinitions || isFinance) && (
        <>
          <Divider
            component="li"
            sx={{ display: `${open ? 'none' : 'block'}`, borderBottomWidth: 2 }}
          />

          {open && (
            <Typography variant="body2" ml={2.5} mt={1} mb={0.5}>
              Administration
            </Typography>
          )}

          {canManageKpiDefinitions && (
            <NavigationLink
              to={paths.kpiDefinitions}
              icon={<WorkspacePremiumOutlinedIcon />}
              title="KPI definitions"
              open={open}
            />
          )}

          {isFinance && (
            <NavigationLink
              to={paths.clientInvoice}
              icon={<RequestQuoteOutlinedIcon />}
              title="Client invoices"
              open={open}
            />
          )}
        </>
      )}
      {(isFinance || hasMarginTrackerAccess) && (
        <>
          <Divider
            component="li"
            sx={{ display: `${open ? 'none' : 'block'}`, borderBottomWidth: 2 }}
          />

          {open && (
            <Typography variant="body2" ml={2.5} mb={0.5}>
              Portfolio
            </Typography>
          )}

          <NavigationLink
            to={paths.portfolio}
            icon={<MonitorHeartOutlinedIcon />}
            title={'My portfolio'}
            open={open}
          />
        </>
      )}

      {(isFinance || hasMarginTrackerAccess || isRevenueSummaryAdmin) && (
        <NavigationLink
          to={paths.revenueStatus}
          icon={<PieChartOutlineIcon />}
          title={'Quarterly revenue'}
          open={open}
        />
      )}

      {(isFinance || hasMarginTrackerAccess) && (
        <>
          <NavigationLink
            to={paths.sowAdmin}
            icon={<SowManagementIcon />}
            title={'SOW management'}
            open={open}
          />

          <NavigationLink
            to={paths.recentSowChanges}
            icon={<HistoryIcon />}
            title={'Change requests'}
            open={open}
          />
        </>
      )}

      <Divider
        component="li"
        sx={{ display: `${open ? 'none' : 'block'}`, borderBottomWidth: 2 }}
      />

      <ListItemButton
        onClick={handleSecondaryOptionsToggle}
        sx={{
          pl: 0,
          pt: 0,
          pb: 0,
          mb: 0,
          borderRadius: 1,
          pr: 0,
          justifyContent: 'center',
        }}
      >
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          {isSecondaryNavOpen ? <KeyboardArrowUpRounded /> : <MenuRounded />}
        </ListItemIcon>
        <ListItemText
          primary="More"
          primaryTypographyProps={{
            mb: '2px',
          }}
          secondary="Sign out, terms etc."
          sx={{
            opacity: open ? 1 : 0,
            width: open ? 'auto' : 0,
            transition: open
              ? 'opacity 175ms ease-out 200ms, width 150ms ease-out'
              : 'opacity 100ms ease-in, width 100ms ease-in',
          }}
        />
      </ListItemButton>
      {isSecondaryNavOpen && (
        <>
          <NavigationLink
            to={paths.termsOfUse}
            icon={<SecurityIcon />}
            title={'Terms of use'}
            open={open}
          />
          <ListItemButton
            onClick={handleLogout}
            sx={{
              ml: open ? 0.4 : 0.6,
              pl: 0,
              py: 0,
              borderRadius: 1,
              pr: open ? 2 : 0,
              justifyContent: open ? 'flex-start' : 'center',
            }}
          >
            <Tooltip
              title="Sign out"
              placement="right"
              disableHoverListener={open}
            >
              <ListItemIcon sx={{ justifyContent: 'center' }}>
                <LogoutIcon />
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary="Sign out"
              sx={{
                whiteSpace: 'nowrap',
                opacity: open ? 1 : 0,
                width: open ? 'auto' : 0,
                transition: open
                  ? 'opacity 175ms ease-out 200ms, width 150ms ease-out'
                  : 'opacity 100ms ease-in, width 100ms ease-in',
              }}
            />
          </ListItemButton>
        </>
      )}
    </List>
  );
};

export default Navigation;
